import { User } from '../types';

export const Permissions = {
  // User permissions
  USER_CREATE: 'user:create',
  USER_READ: 'user:read',
  USER_UPDATE: 'user:update',
  USER_DELETE: 'user:delete',
  USER_EXPORT: 'user:export',
  USER_MANAGE: 'user:manage',

  // Event permissions
  EVENT_CREATE: 'event:create',
  EVENT_READ: 'event:read',
  EVENT_UPDATE: 'event:update',
  EVENT_DELETE: 'event:delete',
  EVENT_MANAGE: 'event:manage',

  // Exhibitor permissions
  EXHIBITOR_CREATE: 'exhibitor:create',
  EXHIBITOR_READ: 'exhibitor:read',
  EXHIBITOR_UPDATE: 'exhibitor:update',
  EXHIBITOR_DELETE: 'exhibitor:delete',
  EXHIBITOR_EXPORT: 'exhibitor:export',
  EXHIBITOR_MANAGE: 'exhibitor:manage',

  // Delegate permissions
  DELEGATE_CREATE: 'delegate:create',
  DELEGATE_READ: 'delegate:read',
  DELEGATE_UPDATE: 'delegate:update',
  DELEGATE_DELETE: 'delegate:delete',
  DELEGATE_EXPORT: 'delegate:export',
  DELEGATE_MANAGE: 'delegate:manage',

  // Session permissions
  SESSION_CREATE: 'session:create',
  SESSION_READ: 'session:read',
  SESSION_UPDATE: 'session:update',
  SESSION_DELETE: 'session:delete',
  SESSION_MANAGE: 'session:manage',

  // Floor Plan permissions
  FLOOR_CREATE: 'session:create',
  FLOOR_READ: 'session:read',
  FLOOR_UPDATE: 'session:update',
  FLOOR_DELETE: 'session:delete',
  FLOOR_MANAGE: 'session:manage',

  // Lead permissions
  LEAD_CREATE: 'lead:create',
  LEAD_READ: 'lead:read',
  LEAD_UPDATE: 'lead:update',
  LEAD_DELETE: 'lead:delete',
  LEAD_MANAGE: 'lead:manage',

  // Dashboard permissions
  DASHBOARD_VIEW: 'dashboard:read',
  DASHBOARD_ANALYTICS: 'dashboard:analytics',

  // Report permissions
  REPORT_CREATE: 'report:create',
  REPORT_READ: 'report:read',
  REPORT_EXPORT: 'report:export',

  // Settings permissions
  SETTINGS_READ: 'settings:read',
  SETTINGS_UPDATE: 'settings:update',
  SETTINGS_MANAGE: 'settings:manage',

  // Notification permissions
  NOTIFICATION_CREATE: 'notification:create',
  NOTIFICATION_READ: 'notification:read',
  NOTIFICATION_SEND: 'notification:send',
  NOTIFICATION_MANAGE: 'notification:manage',

  // ESC permissions
  ESC_CREATE: 'esc_calendar:create',
  ESC_READ: 'esc_calendar:read',
  ESC_SEND: 'esc_calendar:send',
  ESC_MANAGE: 'esc_calendar:manage',

  // Floor image
  FLOOR_PLAN_CREATE: 'floor_plan:create',
  FLOOR_PLAN_READ: 'floor_plan:read',
  FLOOR_PLAN_SEND: 'floor_plan:send',
  FLOOR_PLAN_MANAGE: 'floor_plan:manage',

  //guest
  GUEST_READ: 'guest:create',
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];

export function hasPermission(
  user: User | null,
  permission: Permission
): boolean {
  if (!user || !user.role || !user.role.permissions) {
    return false;
  }

  return user.role.permissions.some(p => p.name === permission);
}

export function hasAnyPermission(
  user: User | null,
  permissions: Permission[]
): boolean {
  if (!user || !user.role || !user.role.permissions) {
    return false;
  }

  const userPermissions = user.role.permissions.map(p => p.name);
  return permissions.some(permission => userPermissions.includes(permission));
}

export function hasAllPermissions(
  user: User | null,
  permissions: Permission[]
): boolean {
  if (!user || !user.role || !user.role.permissions) {
    return false;
  }

  const userPermissions = user.role.permissions.map(p => p.name);
  return permissions.every(permission => userPermissions.includes(permission));
}

export function isAdmin(user: User | null): boolean {
  return user?.role?.name === 'admin' || false;
}

export function isStaff(user: User | null): boolean {
  console.log('user?.role', user?.role);
  return user?.role?.name === 'staff' || false;
}

export function canManageUsers(user: User | null): boolean {
  return hasAnyPermission(user, [
    Permissions.USER_MANAGE,
    Permissions.USER_CREATE,
    Permissions.USER_UPDATE,
    Permissions.USER_DELETE,
  ]);
}

export function canManageEvents(user: User | null): boolean {
  return hasAnyPermission(user, [
    Permissions.EVENT_MANAGE,
    Permissions.EVENT_CREATE,
    Permissions.EVENT_UPDATE,
    Permissions.EVENT_DELETE,
  ]);
}

export function canManageExhibitors(user: User | null): boolean {
  return hasAnyPermission(user, [
    Permissions.EXHIBITOR_MANAGE,
    Permissions.EXHIBITOR_CREATE,
    Permissions.EXHIBITOR_UPDATE,
    Permissions.EXHIBITOR_DELETE,
  ]);
}

export function canViewDashboard(user: User | null): boolean {
  return hasPermission(user, Permissions.DASHBOARD_VIEW);
}

export function canExportData(user: User | null): boolean {
  return hasAnyPermission(user, [
    Permissions.USER_EXPORT,
    Permissions.EXHIBITOR_EXPORT,
    Permissions.DELEGATE_EXPORT,
    Permissions.REPORT_EXPORT,
  ]);
}

/////////////for conference////////////////
export function canManageConference(user: User | null): boolean {
  return hasAnyPermission(user, [
    Permissions.SESSION_MANAGE,
    Permissions.SESSION_CREATE,
    Permissions.SESSION_UPDATE,
    Permissions.SESSION_DELETE,
  ]);
}
